// API client central — baseURL, autenticação Bearer com refresh automático,
// e wrappers high-level por domínio (chat, pedidos, equipamentos, etc).
//
// Refresh: quando uma chamada retorna 401, tenta `/auth/refresh` com o refresh
// token do localStorage; se conseguir, repete a requisição original; se não,
// lança 'SESSAO_EXPIRADA' pra UI redirecionar pro login.
//
// Contexto de operação: helper interno `_ctxQs` lê `toalugando_op_ctx` do
// localStorage (gravado pelo OperandoComoProvider) e anexa querystring
// `?empresa_id=X` ou `?como_pf=true` nos endpoints que precisam.

import { Sentry } from '../services/sentry';

export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Timeout default pra chamadas HTTP. Antes não havia limite — se o backend
// caísse no meio de um pagamento ou KYC, o usuário ficava com spinner
// rodando indefinidamente. 60s cobre upload de KYC e EFI; pra uploads
// maiores, o caller pode passar opts.timeout.
const DEFAULT_TIMEOUT_MS = 60_000;

export const api = {
  _token: null,
  _refreshing: false,

  setToken(token) {
    this._token = token;
    try { localStorage.setItem('toalugando_token', token); } catch(e){}
  },

  setRefreshToken(token) {
    try { localStorage.setItem('toalugando_refresh', token); } catch(e){}
  },

  loadToken() {
    try { this._token = localStorage.getItem('toalugando_token'); } catch(e){}
  },

  clearTokens() {
    this._token = null;
    try {
      localStorage.removeItem('toalugando_token');
      localStorage.removeItem('toalugando_refresh');
    } catch(e){}
  },

  setTokens(access, refresh) {
    this.setToken(access);
    if (refresh) this.setRefreshToken(refresh);
  },

  // Renovação ÚNICA e compartilhada: duas requisições com token vencido ao
  // mesmo tempo esperam a mesma renovação. Antes a 2ª via "já renovando",
  // desistia e acusava SESSAO_EXPIRADA falso. E erro do SERVIDOR (5xx, rede)
  // não é sessão expirada — devolve 'indisponivel' em vez de deslogar
  // (auditoria 29/10, A8-05).
  _refreshPromise: null,
  async _tryRefresh() {
    if (this._refreshPromise) return this._refreshPromise;
    const refreshToken = localStorage.getItem('toalugando_refresh');
    if (!refreshToken) return false;
    this._refreshing = true;
    this._refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (res.status >= 500) return 'indisponivel';
        if (!res.ok) return false;
        const data = await res.json();
        this.setToken(data.access_token);
        if (data.refresh_token) this.setRefreshToken(data.refresh_token);
        return true;
      } catch(e) {
        return 'indisponivel';
      } finally {
        this._refreshing = false;
        this._refreshPromise = null;
      }
    })();
    return this._refreshPromise;
  },

  async fetch(path, opts = {}) {
    // Não forçar Content-Type quando o body for FormData (browser define o boundary)
    // nem quando NÃO houver body (evita CORS preflight desnecessário em GET/DELETE).
    const isFormData = opts.body instanceof FormData;
    const hasBody = opts.body != null;
    const headers = (!hasBody || isFormData)
      ? { ...opts.headers }
      : { 'Content-Type': 'application/json', ...opts.headers };
    const token = this._token || localStorage.getItem('toalugando_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const timeoutMs = opts.timeout || DEFAULT_TIMEOUT_MS;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    // Remove `timeout` das opts pra não vazar pro fetch nativo
    const { timeout: _t, ...fetchOpts } = opts;

    let res;
    try {
      res = await fetch(`${API_URL}/api${path}`, { ...fetchOpts, headers, signal: ctrl.signal });
    } catch (e) {
      if (e?.name === 'AbortError') {
        throw new Error(`O servidor demorou demais para responder (>${Math.round(timeoutMs/1000)}s). Verifique sua conexão e tente novamente.`);
      }
      throw e;
    } finally {
      clearTimeout(timer);
    }

    if (res.status === 401) {
      const refreshed = await this._tryRefresh();
      if (refreshed === 'indisponivel') {
        throw new Error('Não conseguimos falar com o servidor agora. Tente de novo em instantes.');
      }
      if (refreshed) {
        const newToken = this._token || localStorage.getItem('toalugando_token');
        const retryHeaders = { ...headers, 'Authorization': `Bearer ${newToken}` };
        const retryCtrl = new AbortController();
        const retryTimer = setTimeout(() => retryCtrl.abort(), timeoutMs);
        let retry;
        try {
          retry = await fetch(`${API_URL}/api${path}`, { ...fetchOpts, headers: retryHeaders, signal: retryCtrl.signal });
        } catch (e) {
          if (e?.name === 'AbortError') {
            throw new Error(`O servidor demorou demais para responder (>${Math.round(timeoutMs/1000)}s). Verifique sua conexão e tente novamente.`);
          }
          throw e;
        } finally {
          clearTimeout(retryTimer);
        }
        if (retry.status === 401) throw new Error('SESSAO_EXPIRADA');
        this._aposMutacao(path, opts, retry);
        this._avisar5xx(path, opts, retry);
        return retry;
      }
      throw new Error('SESSAO_EXPIRADA');
    }
    this._aposMutacao(path, opts, res);
    this._avisar5xx(path, opts, res);
    return res;
  },

  // Resposta 5xx: manda pro Sentry do front com rota, status e o `detail`/`ref`
  // que o backend devolveu (app/core/erros.py) — junta o erro que o usuário
  // viu com o evento do servidor no painel. Nunca interfere na resposta.
  _avisar5xx(path, opts, res) {
    if (!res || res.status < 500) return;
    const rota = path.split('?')[0].replace(/[0-9a-f]{8}-[0-9a-f-]{27}/gi, ':id').replace(/\/\d+(?=\/|$)/g, '/:n');
    res.clone().json().catch(() => ({})).then((d) => {
      try {
        Sentry.captureMessage(`API ${res.status} ${opts.method || 'GET'} ${rota}`, {
          level: 'error',
          fingerprint: ['api-5xx', String(res.status), opts.method || 'GET', rota],
          tags: { http_status: String(res.status), api_ref: d?.ref || 'sem-ref' },
          extra: { detail: typeof d?.detail === 'string' ? d.detail : JSON.stringify(d).slice(0, 500), path },
        });
      } catch (e) { /* observabilidade nunca derruba a tela */ }
    }).catch(() => {});
  },

  get(path) { return this.fetch(path); },
  post(path, data) { return this.fetch(path, { method: 'POST', body: JSON.stringify(data) }); },
  put(path, data) { return this.fetch(path, { method: 'PUT', body: JSON.stringify(data) }); },
  patch(path, data) { return this.fetch(path, { method: 'PATCH', body: JSON.stringify(data) }); },
  del(path) { return this.fetch(path, { method: 'DELETE' }); },

  // Chat/Mensagens API
  async listarConversas() {
    const res = await this.get('/chat/conversas');
    return res.json();
  },
  async listarMensagens(conversaId, limit = 50) {
    const res = await this.get(`/chat/conversas/${conversaId}/mensagens?limit=${limit}`);
    return res.json();
  },
  async cartaoUsuarioChat(usuarioId) {
    const res = await this.get(`/chat/usuarios/${usuarioId}/cartao`);
    return res.json();
  },
  async enviarMensagem(destinatarioId, conteudo, pedidoId = null) {
    const res = await this.post('/chat/mensagens', {
      destinatario_id: destinatarioId,
      conteudo,
      pedido_id: pedidoId,
    });
    const d = await res.json().catch(() => ({}));
    // Recusa (ex.: bloqueio entre usuários) vira erro com o motivo — antes o
    // JSON de erro entrava na conversa como se fosse uma mensagem vazia
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Falha ao enviar');
    return d;
  },
  // Contador de CHAT não lido ({mensagens, conversas}) — fonte do badge da aba
  // Chat no rodapé. Só mensagens onde EU sou o destinatário; nunca as que eu
  // mesmo enviei, e nada de notificação de status (essas são do sino do topo).
  async chatNaoLidas() {
    try {
      const res = await this.get('/chat/nao-lidas');
      if (!res.ok) return { mensagens: 0, conversas: 0 };
      const d = await res.json();
      return { mensagens: d?.mensagens || 0, conversas: d?.conversas || 0 };
    } catch { return { mensagens: 0, conversas: 0 }; }
  },

  _ctxQs(ctx) {
    let c = ctx;
    if (!c) {
      try {
        const raw = localStorage.getItem('toalugando_op_ctx');
        if (raw) c = JSON.parse(raw);
      } catch(e) {}
    }
    if (!c) return '';
    if (c.empresa_id) return `?empresa_id=${c.empresa_id}`;
    // como_pf=true NAO eh enviado por default - o filtro no backend (so mostrar
    // pedidos onde tanto locataria QUANTO locadora sao PF) eh restritivo demais
    // e escondia pedidos validos do user. Backend continua suportando o param
    // pra quem mandar explicitamente, mas o cliente nao envia automaticamente.
    return '';
  },

  // ── Dedup + micro-cache dos GETs quentes ───────────────────────────
  // A aba Atividade tem 3 consumidores de /pedidos/ (aprovar, em andamento e
  // devolução) + o hub de notificações: eram 3-4 requests IDÊNTICOS por ciclo
  // no endpoint mais pesado do app (parte da lentidão relatada). Agora a 2ª
  // chamada simultânea reaproveita a MESMA promessa e há cache de 1,5s.
  _inflight: new Map(),
  _cacheGet: new Map(),
  // Último payload BOM por chave. Diferente do _cacheGet (frescor, apagado a
  // cada mutação), este só é usado como rede de segurança quando a rede/API
  // falha — e por isso SOBREVIVE à invalidação: invalidar é sobre frescor,
  // não sobre validade. Sem ele, um refetch pós-mutação que falhasse voltava
  // a zerar a tela.
  _ultimoBom: new Map(),
  _ULTIMO_BOM_MAX_MS: 10 * 60 * 1000, // depois disso, melhor errar que mentir
  // Geração do cache. Toda invalidação incrementa; uma resposta que saiu ANTES
  // da mutação não pode mais entrar no cache depois dela.
  _geracao: 0,

  _pegaUltimoBom(path) {
    const b = this._ultimoBom.get(path);
    if (b && Date.now() - b.t < this._ULTIMO_BOM_MAX_MS) return b.data;
    return null;
  },

  async getJsonDedup(path, ttlMs = 1500) {
    const hit = this._cacheGet.get(path);
    if (hit && Date.now() - hit.t < ttlMs) return hit.data;
    const emVoo = this._inflight.get(path);
    if (emVoo) return emVoo;
    const geracaoNaPartida = this._geracao;
    const p = (async () => {
      let res;
      try {
        res = await this.get(path);
      } catch (e) {
        // Sessão expirada precisa PROPAGAR (dispara o fluxo de re-login);
        // qualquer outra falha de rede — o caso real do celular — devolve o
        // último dado bom em vez de zerar a tela.
        if (String(e?.message || '').includes('SESSAO_EXPIRADA')) throw e;
        const bom = this._pegaUltimoBom(path);
        if (bom) return bom;
        throw e;
      }
      if (!res.ok) {
        // NÃO cacheia falha. Antes um 500 momentâneo virava [] em cache por
        // 1,5s e a tela do cliente "perdia" todos os pedidos; devolver o
        // último dado bom mantém a lista de pé até o próximo ciclo.
        const bom = this._pegaUltimoBom(path);
        return bom || [];
      }
      const data = await res.json();
      this._ultimoBom.set(path, { t: Date.now(), data });
      // Se houve mutação enquanto esta resposta estava no ar, ela já nasceu
      // velha: entrega pra quem pediu, mas não envenena o cache — senão o
      // refetch pós-ação recebia o estado ANTERIOR e o cartão ficava travado
      // na etapa antiga por ~2s (o locador clicava de novo achando que falhou).
      if (geracaoNaPartida === this._geracao) {
        this._cacheGet.set(path, { t: Date.now(), data });
      }
      return data;
    })().finally(() => {
      // Só remove a PRÓPRIA entrada: após uma invalidação, um request NOVO
      // pode já ter registrado esta chave — o finally do antigo apagava a
      // entrada do novo e o dedup deixava de deduplicar.
      if (this._inflight.get(path) === p) this._inflight.delete(path);
    });
    this._inflight.set(path, p);
    return p;
  },

  invalidarCachePedidos() {
    this._geracao += 1;
    for (const k of Array.from(this._cacheGet.keys())) {
      if (k.startsWith('/pedidos/') || k.startsWith('/entregas/')) this._cacheGet.delete(k);
    }
    // Descarta também as promessas EM VOO: sem isto, o refetch disparado logo
    // após a mutação era atendido pela requisição que saiu antes dela.
    for (const k of Array.from(this._inflight.keys())) {
      if (k.startsWith('/pedidos/') || k.startsWith('/entregas/')) this._inflight.delete(k);
    }
  },

  // Toda mutação de pedido/entrega limpa o cache e avisa a UI inteira. Antes
  // só "aceitar pedido" avisava — confirmar retirada, marcar devolvido e
  // iniciar entrega deixavam a tela do outro lado congelada até dar F5.
  _aposMutacao(path, opts, res) {
    try {
      const m = (opts?.method || 'GET').toUpperCase();
      if (m === 'GET' || !res?.ok) return;
      if (!/^\/(pedidos|entregas)/.test(path)) return;
      this.invalidarCachePedidos();
      window.dispatchEvent(new Event('toalugando:pedidos:mudou'));
    } catch {}
  },

  // Pedidos API
  async listarPedidos(ctx) {
    return this.getJsonDedup(`/pedidos/${this._ctxQs(ctx)}`);
  },
  // Erro vira exceção com o motivo do servidor: antes o JSON de erro voltava
  // como se fosse sucesso e o popup seguia como "aceito" (auditoria 29/10, A8-08)
  async aceitarPedido(pedidoId) {
    const res = await this.post(`/pedidos/${pedidoId}/aceitar`);
    return this._jsonOuErro(res, 'Não foi possível aceitar o pedido.');
  },
  async recusarPedido(pedidoId, motivo = 'Indisponibilidade') {
    const res = await this.post(`/pedidos/${pedidoId}/recusar?motivo=${encodeURIComponent(motivo)}`);
    return this._jsonOuErro(res, 'Não foi possível recusar o pedido.');
  },
  async _jsonOuErro(res, padrao) {
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : padrao);
    return d;
  },
  async cancelarPedido(pedidoId) {
    const res = await this.post(`/pedidos/${pedidoId}/cancelar`);
    return res.json();
  },

  // Notificações
  async listarNotificacoes() {
    const res = await this.get('/notificacoes/');
    return res.json();
  },
  async marcarNotificacaoLida(id) {
    const res = await this.post(`/notificacoes/${id}/ler`);
    return res.json();
  },
  async marcarTodasNotificacoesLidas() {
    const res = await this.post('/notificacoes/ler-todas');
    return res.json();
  },

  // Equipamentos
  async getEquipamento(id) {
    const res = await this.get(`/equipamentos/${id}`);
    return res.json();
  },
  // Vínculos — acessórios (pagos, sugeridos) e componentes (grátis, acompanham)
  // de um produto. Modelo do Gestão replicado (dono, 10/09/2026).
  async getVinculados(id, { todos = false } = {}) {
    // todos=true (só o dono) inclui acessórios pausados — usado na edição
    const res = await this.get(`/equipamentos/${id}/vinculados${todos ? '?todos=true' : ''}`);
    if (!res.ok) return { componentes: [], acessorios: [] };
    return res.json();
  },
  // Frete próprio do locador pessoa física (tabela por faixa de km, 15/09/2026)
  async getFreteProprio() {
    const res = await this.fetch('/usuarios/me/locadora/frete');
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível carregar seu frete.');
    return d;
  },
  async salvarFreteProprio(tabela) {
    const res = await this.fetch('/usuarios/me/locadora/frete', { method: 'PUT', body: JSON.stringify({ frete_proprio: tabela }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível salvar seu frete.');
    return d;
  },
  // Formas de receber de cada produto (leve, não conta visualização)
  async modalidadesEntrega(ids) {
    const nada = { chave_ativa: false, modalidades: {} };
    try {
      const res = await this.fetch('/frete/modalidades', { method: 'POST', body: JSON.stringify({ equipamento_ids: ids }) });
      if (!res.ok) return nada;
      return await res.json().catch(() => nada);
    } catch { return nada; } // rede caiu: não mexe no carrinho
  },
  async salvarVinculo(paiId, filhoId, { cobrar = false, qtd = 1, obrigatorio } = {}) {
    const res = await this.put(`/equipamentos/${paiId}/vinculos/${filhoId}`, { cobrar, qtd, obrigatorio });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível salvar o vínculo.');
    return d;
  },
  async removerVinculo(paiId, filhoId) {
    const res = await this.del(`/equipamentos/${paiId}/vinculos/${filhoId}`);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível remover o vínculo.');
    return d;
  },

  // Entregas
  async aceitarEntrega(entregaId) {
    const res = await this.post('/entregas/aceitar', { entrega_id: entregaId });
    const data = await res.json().catch(() => ({}));
    // 409 = outro freteiro pegou primeiro — o caller PRECISA saber
    if (!res.ok) throw new Error(data.detail || 'Não foi possível aceitar esta corrida');
    return data;
  },
  async recusarEntrega(entregaId) {
    const res = await this.post(`/entregas/${entregaId}/recusar`);
    return res.json();
  },
  async atualizarLocalizacao(entregaId, lat, lng) {
    const res = await this.put(`/entregas/${entregaId}/localizacao`, { lat, lng });
    return res.json();
  },
  async getEntrega(entregaId) {
    const res = await this.get(`/entregas/${entregaId}`);
    return res.json();
  },
  // Código de handoff (PIN) do meu lado: locador→coleta, locatário→entrega.
  // Aceita id da entrega OU do pedido. Freteiro nunca recebe código.
  async meuCodigoHandoff(entregaOuPedidoId) {
    const res = await this.get(`/entregas/${entregaOuPedidoId}/meu-codigo`);
    return res.json();
  },

  // Sequential offers (Uber-style)
  async ofertaPendenteFreteiro() {
    const res = await this.get('/entregas/oferta-pendente');
    return res.json();
  },
  async aceitarOferta(ofertaId) {
    const res = await this.post(`/entregas/ofertas/${ofertaId}/aceitar`);
    const data = await res.json().catch(() => ({}));
    // 409 = oferta expirou ou corrida já foi pega — o caller PRECISA saber
    if (!res.ok) throw new Error(data.detail || 'Esta corrida não está mais disponível');
    return data;
  },
  async recusarOferta(ofertaId, motivo = null) {
    const qs = motivo ? `?motivo=${encodeURIComponent(motivo)}` : '';
    const res = await this.post(`/entregas/ofertas/${ofertaId}/recusar${qs}`);
    return res.json();
  },

  // Usuários
  async trocarPerfil(tipo) {
    const res = await this.post(`/usuarios/me/trocar-perfil?tipo=${tipo}`);
    return res.json();
  },
  async updateMe(dados) {
    const res = await this.put('/usuarios/me', dados);
    // 4xx/5xx: lança — as telas mostravam "salvo" (e uma mesclava o erro no
    // usuário) com o servidor recusando (auditoria 29/10, A8-08)
    return this._jsonOuErro(res, 'Não foi possível salvar. Tente de novo.');
  },

  // Equipamentos do locador
  async meusEquipamentos(ctx) {
    const res = await this.get(`/equipamentos/meus${this._ctxQs(ctx)}`);
    return res.json();
  },
  // res.ok obrigatório: api.fetch não lança em 4xx/5xx — sem isto a tela
  // mostrava "Anúncio pausado/excluído" mesmo com a API recusando, e tudo
  // voltava ao recarregar (auditoria 04/08).
  async atualizarEquipamento(id, dados) {
    const res = await this.put(`/equipamentos/${id}`, dados);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível salvar o anúncio.');
    return d;
  },
  async deletarEquipamento(id) {
    const res = await this.del(`/equipamentos/${id}`);
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível excluir o anúncio.');
    }
    return res;
  },
  // Cadastros incompletos (pendências de peso/dim/endereço — bloqueia freteiro)
  async meusEquipamentosIncompletos() {
    const res = await this.get('/equipamentos/me/incompletos');
    return res.json();
  },
  async completarLoteEquipamentos(itens) {
    const res = await this.post('/equipamentos/me/completar-lote', { itens });
    return res.json();
  },
  async importBulkEquipamentos({ itens, empresaId }) {
    const res = await this.post('/equipamentos/me/import-bulk', {
      itens,
      empresa_id: empresaId || null,
    });
    return res.json();
  },

  // Avaliações
  async avaliacoesUsuario(userId) {
    const res = await this.get(`/avaliacoes/usuario/${userId}`);
    return res.json();
  },
  async criarAvaliacao(dados) {
    const res = await this.post('/avaliacoes/', dados);
    const d = await res.json().catch(() => ({}));
    // Sem res.ok a tela exibia "Avaliação Enviada!" mesmo com 400 (pedido já
    // avaliado / inelegível) e nada era gravado (auditoria 04/08).
    if (!res.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível enviar a avaliação.');
    return d;
  },

  // Habilitar perfis
  async verificarVeiculo(crlvFile, fotoFrontalFile) {
    const token = this._token || localStorage.getItem('toalugando_token');
    const formData = new FormData();
    formData.append('documento', crlvFile);
    formData.append('foto_frontal', fotoFrontalFile);
    const res = await fetch(`${API_URL}/api/veiculos/verificar-veiculo`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Erro ao verificar veículo');
    return data;
  },

  // ── Pacotes ─────────────────────────────────────────────────────────
  async meusPacotes(ctx) {
    const r = await this.fetch(`/pacotes/meus${this._ctxQs(ctx)}`);
    return r.ok ? r.json() : [];
  },
  async listarPacotes(params = {}) {
    const qs = new URLSearchParams(params).toString();
    const r = await this.fetch(`/pacotes/${qs ? '?' + qs : ''}`);
    return r.ok ? r.json() : [];
  },
  async getPacote(id) {
    const r = await this.fetch(`/pacotes/${id}`);
    return r.ok ? r.json() : null;
  },
  async criarPacote(dados) {
    const r = await this.fetch('/pacotes/', { method: 'POST', body: JSON.stringify(dados) });
    const d = await r.json();
    if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Erro ao criar pacote');
    return d;
  },
  async atualizarPacote(id, dados) {
    const r = await this.put(`/pacotes/${id}`, dados);
    const d = await r.json();
    if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Erro ao atualizar pacote');
    return d;
  },
  async excluirPacote(id) {
    const r = await this.del(`/pacotes/${id}`);
    return r.ok;
  },

  // ── Localização e distância ────────────────────────────────────────
  async distanciaEquipamento(equipamentoId, cepLocatario) {
    const r = await this.fetch('/localizacao/distancia', {
      method: 'POST',
      body: JSON.stringify({ equipamento_id: equipamentoId, cep_locatario: cepLocatario }),
    });
    return r.ok ? r.json() : { faixa: null, indisponivel: true };
  },
  async distanciaLote(equipamentoIds, cepLocatario) {
    const r = await this.fetch('/localizacao/distancia/lote', {
      method: 'POST',
      body: JSON.stringify({ equipamento_ids: equipamentoIds, cep_locatario: cepLocatario }),
    });
    return r.ok ? r.json() : {};
  },
  async localizacoesSalvas() {
    const r = await this.fetch('/localizacao/minhas');
    return r.ok ? r.json() : [];
  },
  async criarLocalizacao(rotulo, cep, referencia) {
    const r = await this.fetch('/localizacao', {
      method: 'POST',
      body: JSON.stringify({ rotulo, cep, referencia: referencia || null }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Erro ao salvar');
    return d;
  },
  async excluirLocalizacao(id) {
    const r = await this.fetch(`/localizacao/${id}`, { method: 'DELETE' });
    return r.ok;
  },

  // ── Perguntas no anúncio do equipamento ────────────────────────────
  async perguntasListar(equipamentoId) {
    const r = await this.fetch(`/perguntas/equipamento/${equipamentoId}`);
    return r.ok ? r.json() : [];
  },
  async perguntasCriar(equipamentoId, pergunta) {
    const r = await this.fetch('/perguntas', {
      method: 'POST',
      body: JSON.stringify({ equipamento_id: equipamentoId, pergunta }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Não foi possível enviar a pergunta');
    return d;
  },
  async perguntasResponder(perguntaId, resposta) {
    const r = await this.fetch(`/perguntas/${perguntaId}/responder`, {
      method: 'PATCH',
      body: JSON.stringify({ resposta }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || 'Não foi possível responder');
    return d;
  },
  async perguntasUtil(perguntaId) {
    const r = await this.fetch(`/perguntas/${perguntaId}/util`, { method: 'POST' });
    return r.ok ? r.json() : { ok: false };
  },
  async perguntasDestacar(perguntaId, destacada) {
    const r = await this.fetch(`/perguntas/${perguntaId}/destacar`, {
      method: 'PATCH',
      body: JSON.stringify({ destacada }),
    });
    return r.ok ? r.json() : { ok: false };
  },
  async perguntasExcluir(perguntaId) {
    const r = await this.fetch(`/perguntas/${perguntaId}`, { method: 'DELETE' });
    return r.ok;
  },
  async perguntasPendentesLocador() {
    const r = await this.fetch('/perguntas/locador/pendentes');
    return r.ok ? r.json() : [];
  },

  // ── Moderação: denunciar e bloquear (25/10/2026) ───────────────────
  async _moderacao(caminho, metodo = 'GET', corpo) {
    const r = await this.fetch(caminho, { method: metodo, ...(corpo ? { body: JSON.stringify(corpo) } : {}) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof d?.detail === 'string' ? d.detail : 'Não foi possível concluir. Tente de novo.');
    return d;
  },
  denunciar(alvoTipo, alvoId, motivo, detalhe) {
    return this._moderacao('/moderacao/denuncias', 'POST', { alvo_tipo: alvoTipo, alvo_id: alvoId, motivo, detalhe: detalhe || null });
  },
  meusBloqueios() { return this._moderacao('/moderacao/bloqueios'); },
  bloquearUsuario(usuarioId) { return this._moderacao('/moderacao/bloqueios', 'POST', { usuario_id: usuarioId }); },
  desbloquearUsuario(usuarioId) { return this._moderacao(`/moderacao/bloqueios/${usuarioId}`, 'DELETE'); },
  adminDenuncias(status = 'aberta') { return this._moderacao(`/moderacao/admin/denuncias?status=${status}`); },
  adminDecidirDenuncia(id, dados) { return this._moderacao(`/moderacao/admin/denuncias/${id}`, 'PATCH', dados); },
  adminReativarAnuncio(equipamentoId) { return this._moderacao(`/moderacao/admin/anuncios/${equipamentoId}/reativar`, 'POST'); },

  // ── Agenda do locador: bloquear datas de um anúncio ────────────────
  agendaBloqueios(equipamentoId) { return this._moderacao(`/agenda/${equipamentoId}/bloqueios`); },
  agendaBloquear(equipamentoId, dados) { return this._moderacao(`/agenda/${equipamentoId}/bloqueios`, 'POST', dados); },
  agendaDesbloquear(equipamentoId, bloqueioId) {
    return this._moderacao(`/agenda/${equipamentoId}/bloqueios/${bloqueioId}`, 'DELETE');
  },

  // ── Cashback / Saldo Tô Alugando ───────────────────────────────────
  async cashbackSaldo() {
    const r = await this.fetch('/usuarios/me/cashback');
    return r.ok ? r.json() : { saldo_centavos: 0, saldo_reais: 0, extrato: [] };
  },
  async cashbackCalcularAplicavel(valorPedidoReais) {
    const r = await this.fetch('/usuarios/me/cashback/calcular-aplicavel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ valor_pedido_reais: valorPedidoReais }),
    });
    return r.ok ? r.json() : { aplicavel_centavos: 0, aplicavel_reais: 0, saldo_total_centavos: 0 };
  },

  async habilitarFreteiro(dados = {}) {
    // Endpoint agora é gateway: só aceita extras de freteiro (KYC base já vem do Particular).
    const params = new URLSearchParams();
    const allowed = ['veiculo_tipo','veiculo_placa','veiculo_modelo','cnh_categoria',
                     'pix_key','tem_rampa','tem_lona','tem_reboque'];
    allowed.forEach(k => { if (dados[k] != null && dados[k] !== '') params.set(k, dados[k]); });
    const res = await this.fetch(`/usuarios/me/habilitar-freteiro?${params}`, { method: 'POST' });
    const data = await res.json();
    if (!res.ok) {
      const det = data.detail;
      if (det && typeof det === 'object' && det.erro === 'kyc_base_incompleto') {
        const e = new Error(det.mensagem || 'KYC base incompleto.');
        e.pendencias = det.pendencias || [];
        e.kycIncompleto = true;
        throw e;
      }
      throw new Error(typeof det === 'string' ? det : (det?.mensagem || 'Erro ao habilitar freteiro'));
    }
    return data;
  },
  async habilitarLocador(cnpj = '', razao_social = '', extras = {}) {
    const params = new URLSearchParams();
    if (cnpj) params.set('cnpj', cnpj);
    if (razao_social) params.set('razao_social', razao_social);
    const extrasMap = {
      raioEntregaKm: 'raio_entrega_km', fazEntrega: 'faz_entrega', fazRetirada: 'faz_retirada',
      clienteRetira: 'cliente_retira', telefone: 'telefone', whatsapp: 'whatsapp',
      emailComercial: 'email_comercial', cep: 'cep', logradouro: 'logradouro',
      bairro: 'bairro', numero: 'numero', complemento: 'complemento',
      cidade: 'cidade', estado: 'estado',
      horarioDias: 'horario_dias', horarioInicio: 'horario_inicio', horarioFim: 'horario_fim',
    };
    Object.entries(extrasMap).forEach(([jsKey, pyKey]) => {
      if (extras[jsKey] != null && extras[jsKey] !== '') params.set(pyKey, extras[jsKey]);
    });
    const res = await this.fetch(`/usuarios/me/habilitar-locador?${params}`, { method: 'POST' });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Erro ao habilitar locador');
    return data;
  },
  async confirmarRecebimento(pedidoId, { fotosEstadoEntrega, observacoes, checklist, codigo, componentesConferidos } = {}) {
    const res = await this.post(`/pedidos/${pedidoId}/confirmar-recebimento`, {
      fotos_estado_entrega: fotosEstadoEntrega || [],
      observacoes: observacoes || null,
      checklist: checklist || null,
      codigo: codigo || null,
      // Componentes que acompanham, conferidos item a item (ids de itens_pedido)
      componentes_conferidos: componentesConferidos || null,
    });
    const data = await res.json().catch(() => ({}));
    // Sem esta checagem a tela mostrava "Entrega Confirmada!" mesmo com 400/409
    // do servidor (ex.: "aguarde o locador preparar", prazo de vistoria
    // encerrado) — sucesso falso (verificação 28/07).
    if (!res.ok) throw new Error(data.detail || 'Não foi possível confirmar o recebimento.');
    return data;
  },
  async uploadFotoVistoria(pedidoId, file, fase = 'entrega') {
    const token = this._token || localStorage.getItem('toalugando_token');
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(
      `${API_URL}/api/uploads/pedidos/${pedidoId}/vistoria-foto?fase=${encodeURIComponent(fase)}`,
      {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Falha ao subir foto de vistoria');
    }
    return res.json();
  },
  async marcarDevolvido(pedidoId, { fotosEstadoDevolucao, observacoes, aprovarCaucao = true, codigoDevolucao, componentesConferidos } = {}) {
    const res = await this.post(`/pedidos/${pedidoId}/marcar-devolvido`, {
      fotos_estado_devolucao: fotosEstadoDevolucao || [],
      observacoes: observacoes || null,
      aprovar_caucao: aprovarCaucao,
      codigo_devolucao: codigoDevolucao || null,
      // Faltar componente BLOQUEIA a devolução (dono, 10/09/2026)
      componentes_conferidos: componentesConferidos || null,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Não foi possível registrar a devolução.');
    return data;
  },

  // ── Devolução em 3 modos (contrato pedidos.devolucao_modo) ─────────
  // presencial (default, fluxo atual) | freteiro_coleta (o LOCATÁRIO paga o
  // frete de volta) | locador_busca (o locador vai buscar). O locatário só
  // SINALIZA — a devolução só vale depois que o locador valida (marcar-devolvido).
  async solicitarDevolucao(pedidoId, modo = 'presencial') {
    const res = await this.post(`/pedidos/${pedidoId}/solicitar-devolucao`, { modo });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Não foi possível solicitar a devolução.');
    return data;
  },
  // LOCADOR responde à busca (locador_busca): aceita ou recusa (com motivo).
  // Recusa desfaz a solicitação — o cliente volta a poder escolher o modo.
  async responderBuscaDevolucao(pedidoId, aceitar, motivo = null) {
    const res = await this.post(`/pedidos/${pedidoId}/devolucao/busca/responder`, { aceitar, motivo });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Não foi possível responder à busca.');
    return data;
  },
  // Cria o pagamento (PIX) do frete de VOLTA — quem paga é o locatário. Retorna
  // o mesmo shape do PIX do checkout ({ qr_code, qr_code_base64, valor, ... }).
  async criarFreteDevolucao(pedidoId) {
    const res = await this.post('/pagamentos/criar-frete-devolucao', { pedido_id: pedidoId });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Não foi possível gerar o pagamento do frete.');
    return data;
  },

  // KYC
  async uploadKYCDocumento(tipo, file) {
    const token = this._token || localStorage.getItem('toalugando_token');
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/usuarios/me/kyc/upload?tipo=${tipo}`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    return res;
  },
  async statusKYC() {
    const res = await this.get('/usuarios/me/kyc/status');
    if (!res.ok) return { kyc_documentos: {} };
    return res.json();
  },

  // Contrato Master
  async assinarContrato(pedidoId) {
    const res = await this.fetch(`/pedidos/${pedidoId}/assinar-contrato`, { method: 'POST' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erro ao assinar contrato'); }
    return res.json();
  },
  async assinarContratoMaster() {
    const res = await this.fetch('/pedidos/assinar-chapeu', { method: 'POST' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erro ao assinar Contrato Master'); }
    return res.json();
  },
  async confirmarKYCPedido(pedidoId) {
    const res = await this.fetch(`/pedidos/${pedidoId}/confirmar-kyc`, { method: 'POST' });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Erro ao confirmar KYC'); }
    return res.json();
  },

  // KYC Locatário — Receita Federal + Face Match (CNH vs Selfie)
  async verificarKYCLocatario(formData) {
    const token = this._token || localStorage.getItem('toalugando_token');
    const res = await fetch(`${API_URL}/api/usuarios/me/kyc/verificar-locatario`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    return res;
  },
};
